import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiGet } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import {
  FileText,
  Users,
  ClipboardCheck,
  Calendar,
  FileSpreadsheet,
  Package,
  CalendarClock,
  GraduationCap,
  ChevronRight,
  TrendingUp,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
  points_per_unit: number
  requires_attachment: boolean
  attachment_type?: string
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked?: boolean
}

const indicatorIcons: Record<string, React.ElementType> = {
  'C.1': Users,           // Meeting
  'C.2': ClipboardCheck,  // Timesheet
  'C.3': Calendar,        // Briefing
  'C.4': FileSpreadsheet, // QAP
  'C.5': FileText,        // Monthly Report
  'C.6': Package,         // Asset
  'C.7': CalendarClock,   // P3C Schedule
  'C.8': GraduationCap,   // Training
}

const indicatorColors: Record<string, string> = {
  'C.1': 'bg-blue-500',
  'C.2': 'bg-green-500',
  'C.3': 'bg-purple-500',
  'C.4': 'bg-orange-500',
  'C.5': 'bg-pink-500',
  'C.6': 'bg-cyan-500',
  'C.7': 'bg-amber-500',
  'C.8': 'bg-indigo-500',
}

// Generate routes for all indicators
const getIndicatorRoute = (code: string) => `/kpi/submit/${code.toLowerCase().replace('.', '-')}`

export default function KPIHubPage() {
  const { user } = useAuthStore()
  const [indicators, setIndicators] = useState<Indicator[]>([])
  const [submissionCounts, setSubmissionCounts] = useState<Record<number, number>>({})
  const [currentPeriod, setCurrentPeriod] = useState<Period | null>(null)
  const [totalScore, setTotalScore] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch indicators
        const indicatorsData = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
        setIndicators(indicatorsData)

        // Fetch current period
        const periodsData = await apiGet<Period[]>('/kpi/periods')
        const activePeriod = periodsData.find(p => !p.is_locked) || periodsData[0]
        setCurrentPeriod(activePeriod || null)

        // Fetch scores for current period (only if we have a period)
        if (activePeriod && activePeriod.id) {
          try {
            const scoresData = await apiGet<{
              scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
              total_score: number
            }>('/kpi/scores', { period_id: activePeriod.id })

            const counts: Record<number, number> = {}
            scoresData.scores.forEach(s => {
              counts[s.indicator.id] = s.submission_count
            })
            setSubmissionCounts(counts)
            setTotalScore(scoresData.total_score)
          } catch (scoreErr) {
            console.error('Failed to load scores:', scoreErr)
            // Continue without scores if they fail to load
          }
        }
      } catch (err) {
        console.error('Failed to load KPI data:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const calculatePoints = (indicator: Indicator) => {
    const count = submissionCounts[indicator.id] || 0
    return Math.min(count * indicator.points_per_unit, indicator.max_points)
  }

  if (isLoading) {
    return (
      <div className="mobile-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="mobile-page">
      {/* Header Card */}
      <div className="mobile-header-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white/80 text-sm">Hello, {user?.name?.split(' ')[0]}</p>
            <h1 className="text-white text-xl font-bold">KPI Dashboard</h1>
          </div>
          <div className="text-right">
            <p className="text-white/80 text-xs">Current Period</p>
            <p className="text-white font-semibold">
              {currentPeriod?.name || `${currentPeriod?.month}/${currentPeriod?.year}`}
            </p>
          </div>
        </div>

        {/* Score Summary */}
        <div className="bg-white/20 backdrop-blur rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Total Score</p>
              <p className="text-white text-3xl font-bold">{totalScore.toFixed(1)} <span className="text-lg font-normal">points</span></p>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Indicators Grid */}
      <div className="px-4 pb-24">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">KPI Indicators</h2>
        <div className="space-y-3">
          {indicators.map((indicator) => {
            const Icon = indicatorIcons[indicator.code] || FileText
            const colorClass = indicatorColors[indicator.code] || 'bg-gray-500'
            const route = getIndicatorRoute(indicator.code)
            const points = calculatePoints(indicator)
            const count = submissionCounts[indicator.id] || 0

            return (
              <Link key={indicator.id} to={route}>
                <div className="kpi-card">
                  <div className="flex items-start gap-3">
                    <div className={`kpi-card-icon ${colorClass}`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">{indicator.code}</span>
                        <span className="text-xs font-semibold text-primary-600">
                          {points.toFixed(1)} pts
                        </span>
                      </div>
                      <h3 className="font-medium text-gray-900 text-sm mt-1 line-clamp-2">
                        {indicator.name}
                      </h3>
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>{count} {count === 1 ? 'submission' : 'submissions'}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
